(function (root) {
  'use strict';

  var script = typeof document !== 'undefined' ? document.currentScript : null;
  var data = script ? script.dataset : {};
  var defaults = {
    labelLimit: 2,
    newEnabled: true,
    newMethod: 'date',
    newLimitDays: 2,
    saleEnabled: true,
    saleType: 'percent',
    stockEnabled: false,
    stockLimit: 10,
    newLabel: 'New arrival',
    percentLabel: '__AMOUNT__% off',
    valueLabel: 'Save __AMOUNT__',
    stockLabel: 'Only __QUANTITY__ left'
  };

  function boolean(value, fallback) {
    return value === undefined ? fallback : value === 'true';
  }

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  var config = {
    labelLimit: number(data.labelLimit, defaults.labelLimit),
    newEnabled: boolean(data.newEnabled, defaults.newEnabled),
    newMethod: data.newMethod || defaults.newMethod,
    newLimitDays: number(data.newLimitDays, defaults.newLimitDays),
    saleEnabled: boolean(data.saleEnabled, defaults.saleEnabled),
    saleType: data.saleType || defaults.saleType,
    stockEnabled: boolean(data.stockEnabled, defaults.stockEnabled),
    stockLimit: number(data.stockLimit, defaults.stockLimit),
    newLabel: data.newLabel || defaults.newLabel,
    percentLabel: data.percentLabel || defaults.percentLabel,
    valueLabel: data.valueLabel || defaults.valueLabel,
    stockLabel: data.stockLabel || defaults.stockLabel
  };

  function money(value, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format(value);
    } catch (error) {
      return String(value);
    }
  }

  function cheapestVariant(product) {
    var nodes = product && product.graphql && product.graphql.rccBadgeVariants;
    var variants = nodes && Array.isArray(nodes.nodes) ? nodes.nodes : [];
    var valid = variants.filter(function (variant) {
      return Number.isFinite(Number(variant && variant.price && variant.price.amount));
    });

    if (valid.length) {
      return valid.reduce(function (cheapest, variant) {
        return Number(variant.price.amount) < Number(cheapest.price.amount) ? variant : cheapest;
      });
    }

    var priceInfo = product && product.priceInfo;
    return priceInfo ? {
      price: { amount: priceInfo.price, currencyCode: priceInfo.currencyCode },
      compareAtPrice: { amount: priceInfo.originalPrice, currencyCode: priceInfo.currencyCode }
    } : null;
  }

  function isPortraitImage(width, height) {
    width = Number(width);
    height = Number(height);
    // Portrait card scans stay below 0.8; square and landscape artwork keeps its white frame.
    return width > 0 && height > 0 && width / height < 0.8;
  }

  // ponytail: the app rewrites the card's class and swaps the <img> during hydration, so
  // re-apply on every mutation rather than racing it. Observer is never disconnected — it
  // dies with the card. Narrow it with attributeFilter if profiling ever blames this.
  function setImageShape(element) {
    var card = element.closest('.rcc-search__product');
    if (!card || card._rccShapeObserver) return;

    function apply() {
      var image = card.querySelector('.rcc-search__image--primary');
      if (!image) return;

      // Attributes are the fast path; intrinsic size is the truth once decoded, and `load`
      // is the one update the observer can't see. Same fn ref, so the listener dedupes.
      var width = Number(image.getAttribute('width')) || image.naturalWidth;
      var height = Number(image.getAttribute('height')) || image.naturalHeight;
      if (!width || !height) return image.addEventListener('load', apply, { once: true });

      var portrait = isPortraitImage(width, height);
      // Guarded so our own write doesn't re-trigger the observer.
      if (card.classList.contains('rcc-search__product--portrait') !== portrait) {
        card.classList.toggle('rcc-search__product--portrait', portrait);
      }
    }

    card._rccShapeObserver = new MutationObserver(apply);
    card._rccShapeObserver.observe(card, { childList: true, subtree: true, attributes: true });
    apply();
  }

  function isNew(product, settings, now) {
    if (!settings.newEnabled) return false;

    if (settings.newMethod === 'tag') {
      return Array.isArray(product.tags) && (product.tags.includes('New') || product.tags.includes('new'));
    }

    if (settings.newMethod === 'collection') {
      var connection = product.graphql && product.graphql.rccBadgeCollections;
      var collections = connection && Array.isArray(connection.nodes) ? connection.nodes : [];
      return collections.some(function (collection) {
        return String(collection && collection.handle || '').split('-').includes('new');
      });
    }

    var createdAt = product.graphql && product.graphql.rccBadgeCreatedAt;
    var created = Date.parse(createdAt || '');
    return Number.isFinite(created) && (now - created) / 86400000 < settings.newLimitDays;
  }

  function buildBadges(product, settings, now) {
    if (!product) return [];

    var badges = [];
    var graphql = product.graphql || {};
    var metafieldLabel = graphql.rccBadgeLabel && graphql.rccBadgeLabel.value;

    if (metafieldLabel) badges.push({ type: 'meta', text: String(metafieldLabel) });
    if (badges.length < settings.labelLimit && isNew(product, settings, now || Date.now())) {
      badges.push({ type: 'new', text: settings.newLabel });
    }

    var variant = cheapestVariant(product);
    var price = Number(variant && variant.price && variant.price.amount);
    var compareAt = Number(variant && variant.compareAtPrice && variant.compareAtPrice.amount);
    var currency = variant && variant.price && variant.price.currencyCode;

    if (badges.length < settings.labelLimit && settings.saleEnabled && compareAt > price) {
      var reduction = compareAt - price;
      var text = settings.saleType === 'percent'
        ? settings.percentLabel.replace('__AMOUNT__', String(Math.round(reduction / compareAt * 100)))
        : settings.valueLabel.replace('__AMOUNT__', money(reduction, currency));
      badges.push({ type: 'sale', text: text });
    }

    var variantConnection = graphql.rccBadgeVariants;
    var variants = variantConnection && Array.isArray(variantConnection.nodes) ? variantConnection.nodes : [];
    var quantity = variants.length === 1 ? variants[0].quantityAvailable : null;
    if (
      badges.length < settings.labelLimit &&
      settings.stockEnabled &&
      Number.isFinite(quantity) &&
      quantity > 0 &&
      (settings.stockLimit === 0 || quantity <= settings.stockLimit)
    ) {
      badges.push({
        type: 'stock',
        text: settings.stockLabel.replace('__QUANTITY__', String(quantity))
      });
    }

    return badges.slice(0, settings.labelLimit);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildBadges: buildBadges,
      defaults: defaults,
      isPortraitImage: isPortraitImage
    };
  }

  if (!root.customElements || !root.HTMLElement || root.customElements.get('rcc-product-badges')) return;

  class RCCProductBadges extends root.HTMLElement {
    static get graphqlRequirements() {
      return {
        graphql: [
          'rccBadgeLabel: metafield(namespace: "theme", key: "label") { value }',
          'rccBadgeCreatedAt: createdAt',
          'rccBadgeCollections: collections(first: 50) { nodes { handle } }',
          'rccBadgeVariants: variants(first: 100) { nodes { availableForSale quantityAvailable price { amount currencyCode } compareAtPrice { amount currencyCode } } }'
        ]
      };
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    get product() {
      return this._product;
    }

    connectedCallback() {
      setImageShape(this);
      this.render();
    }

    render() {
      if (!this._product) return;

      var badges = buildBadges(this._product, config, Date.now());
      this.replaceChildren();
      this.hidden = badges.length === 0;

      badges.forEach(function (badge) {
        var element = document.createElement('span');
        element.className = 'rcc-product-badge rcc-product-badge--' + badge.type;
        element.textContent = badge.text;
        this.appendChild(element);
      }, this);
    }
  }

  root.customElements.define('rcc-product-badges', RCCProductBadges);
})(typeof window !== 'undefined' ? window : globalThis);
